/**
 * Fee Calculator Settings API — read/write the firm-wide fee
 * configuration row (`fee_calculator_settings`, id = 1).
 *
 * SELECT is open to any authenticated user (assistants need to read
 * the values to compute fees in the Tools tab); UPDATE is gated by
 * Supabase RLS to the adviser email. INSERT/DELETE are blocked at
 * the policy level — the row is seeded once via migration.
 *
 * Database column ↔ camelCase mapping:
 *   hourly_rate    ↔ hourlyRate
 *   min_threshold  ↔ minThreshold
 *   fee_cap        ↔ feeCap
 *   vat_rate       ↔ vatRate
 */

import supabase from './supabaseClient';
import { DEFAULTS } from '../components/tools/feeCalculator/feeEngine';

const ROW_ID = 1;

function rowToSettings(row) {
  if (!row) return { ...DEFAULTS };
  return {
    hourlyRate: Number(row.hourly_rate) || DEFAULTS.hourlyRate,
    minThreshold: Number(row.min_threshold) || DEFAULTS.minThreshold,
    feeCap: Number(row.fee_cap) || DEFAULTS.feeCap,
    vatRate: Number(row.vat_rate) || DEFAULTS.vatRate,
  };
}

/**
 * Load the singleton settings row. Falls back to in-app DEFAULTS if
 * the table is unreachable so the calculator stays usable in dev.
 */
export async function getFeeSettings() {
  try {
    const { data, error } = await supabase
      .from('fee_calculator_settings')
      .select('*')
      .eq('id', ROW_ID)
      .maybeSingle();

    if (error) {
      console.warn('[FeeCalcSettings] Load failed, using defaults:', error.message);
      return { ...DEFAULTS };
    }
    return rowToSettings(data);
  } catch (err) {
    console.warn('[FeeCalcSettings] Load error:', err);
    return { ...DEFAULTS };
  }
}

/**
 * Persist the settings row. Adviser only — Supabase RLS will reject
 * the UPDATE if called by any other authenticated user. Returns the
 * new settings (mapped back to camelCase) on success.
 */
export async function saveFeeSettings(next, updatedBy) {
  const payload = {
    hourly_rate: next.hourlyRate,
    min_threshold: next.minThreshold,
    fee_cap: next.feeCap,
    vat_rate: next.vatRate,
    updated_at: new Date().toISOString(),
    updated_by: updatedBy || null,
  };

  const { data, error } = await supabase
    .from('fee_calculator_settings')
    .update(payload)
    .eq('id', ROW_ID)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to save fee calculator settings: ${error.message}`);
  }

  return rowToSettings(data);
}
